import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Spinner } from '../components/ui/Spinner'
import { ColorPicker } from '../components/ui/ColorPicker'
import { cn } from '../lib/utils'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

const PAID_FEATURES = [
  'Unlimited members', 'All features', 'Budget & bills',
  'Health logs', 'Vault', 'Linked households',
]

const PLANS = [
  {
    id:       'free',
    name:     'Free',
    price:    '$0',
    cycle:    null,
    desc:     'Core household tools for one family.',
    features: ['Up to 5 members', 'Calendar & tasks', 'Shopping lists', 'Meal planning'],
    cta:      'Continue free',
    badge:    null,
  },
  {
    id:       'monthly',
    name:     'Paid — Monthly',
    price:    '$4',
    cycle:    '/mo',
    desc:     'Full access, billed monthly.',
    features: PAID_FEATURES,
    cta:      'Start free trial',
    badge:    '7-day trial',
  },
  {
    id:       'annual',
    name:     'Paid — Annual',
    price:    '$40',
    cycle:    '/yr',
    desc:     'Full access, billed annually.',
    features: PAID_FEATURES,
    cta:      'Start free trial',
    badge:    '7-day trial · Save 17%',
  },
  {
    id:       'lifetime',
    name:     'Lifetime',
    price:    '$120',
    cycle:    'one-time',
    desc:     'Pay once, yours forever.',
    features: [...PAID_FEATURES, 'All future updates', 'Priority support'],
    cta:      'Buy lifetime access',
    badge:    'One-time payment',
  },
]

const StepDots = ({ current, total }) => (
  <div className="flex items-center gap-2 justify-center mb-8">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={cn(
          'rounded-full transition-all',
          i === current ? 'w-6 h-2 bg-primary' : 'w-2 h-2 bg-border'
        )}
      />
    ))}
  </div>
)

export const OnboardingPage = () => {
  const { user, refreshHouseholds } = useAuth()
  const navigate      = useNavigate()
  const [params]      = useSearchParams()
  const isNewHousehold = params.get('new') === '1'

  const [step,           setStep]          = useState(0)
  const [householdName,  setHouseholdName] = useState('')
  const [householdId,    setHouseholdId]   = useState(null)
  const [color,          setColor]         = useState('violet')
  const [inviteEmail,    setInviteEmail]   = useState('')
  const [inviteRole,     setInviteRole]    = useState('standard')
  const [inviteToken,    setInviteToken]   = useState(null)
  const [selectedPlan,   setSelectedPlan]  = useState('free')
  const [loading,        setLoading]       = useState(false)
  const [error,          setError]         = useState('')

  // ── Step 0: Name your household ──────────────────────────────────────────
  const handleCreateHousehold = async (e) => {
    e.preventDefault()
    if (!householdName.trim()) { setError('Please enter a name for your household.'); return }
    setError('')
    setLoading(true)

    // Guard against duplicate creation on re-entry (non-new-household flow)
    if (!isNewHousehold) {
      const { data: existing } = await supabase
        .from('household_members')
        .select('households(id)')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle()
      if (existing?.households?.id) {
        setHouseholdId(existing.households.id)
        await refreshHouseholds()
        setLoading(false)
        setStep(1)
        return
      }
    }

    const { data, error: err } = await supabase
      .from('households')
      .insert({ name: householdName.trim(), created_by: user.id })
      .select('id')
      .single()

    setLoading(false)
    if (err) { setError('Could not create household. Please try again.'); return }

    setHouseholdId(data.id)
    await refreshHouseholds()
    setStep(1)
  }

  // ── Step 1: Choose member color ───────────────────────────────────────────
  const handleSaveColor = async () => {
    setLoading(true)
    await supabase.from('profiles').update({ member_color: color }).eq('id', user.id)
    setLoading(false)
    setStep(2)
  }

  // ── Step 2: Invite first member ───────────────────────────────────────────
  const handleInvite = async (e) => {
    e.preventDefault()
    if (!inviteEmail.trim()) { setStep(3); return }  // skip if empty
    setError('')
    setLoading(true)

    const { data, error: err } = await supabase
      .from('invites')
      .insert({ household_id: householdId, email: inviteEmail.trim(), role: inviteRole })
      .select('token')
      .single()

    setLoading(false)
    if (err) { setError('Could not create invite. Please try again.'); return }

    setInviteToken(data.token)
  }

  const inviteLink = inviteToken
    ? `${window.location.origin}/invite/${inviteToken}`
    : null

  const copyInviteLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink) } catch { /* ignore */ }
  }

  // ── Step 3: Choose plan ───────────────────────────────────────────────────
  const handleChoosePlan = async () => {
    if (selectedPlan === 'free') {
      navigate('/calendar', { replace: true })
      return
    }

    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey':        SUPABASE_KEY,
        },
        body: JSON.stringify({
          action:        'checkout',
          billing_cycle: selectedPlan,  // 'monthly' | 'annual' | 'lifetime'
        }),
      })
      const body = await res.json()
      if (body.url) { window.location.href = body.url; return }
      setError('Could not start checkout. Please try again.')
    } catch {
      setError('Network error. Please try again.')
    }
    setLoading(false)
  }

  const TOTAL_STEPS = 4

  return (
    <div className="min-h-full flex flex-col items-center justify-center bg-background px-5 py-12">
      <div className="w-full max-w-sm">
        <StepDots current={step} total={TOTAL_STEPS} />

        {/* ── Step 0: Household name ───────────────────────────────────── */}
        {step === 0 && (
          <div>
            <div className="text-center mb-8">
              <div className="text-5xl mb-4">🏠</div>
              <h1 className="text-2xl font-bold text-text-primary">Name your household</h1>
              <p className="text-sm text-text-muted mt-2">
                {isNewHousehold ? 'Create an additional household' : "Let's get your family set up on DoorChart."}
              </p>
            </div>
            <form onSubmit={handleCreateHousehold} className="space-y-5">
              <Input
                label="Household name"
                type="text"
                placeholder="e.g. The Johnson Family"
                value={householdName}
                onChange={(e) => setHouseholdName(e.target.value)}
                error={error}
                autoFocus
              />
              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? <><Spinner size="sm" /> Creating…</> : 'Continue'}
              </Button>
            </form>
          </div>
        )}

        {/* ── Step 1: Member color ─────────────────────────────────────── */}
        {step === 1 && (
          <div>
            <div className="text-center mb-8">
              <div className="text-5xl mb-4">🎨</div>
              <h1 className="text-2xl font-bold text-text-primary">Pick your color</h1>
              <p className="text-sm text-text-muted mt-2">
                This color represents you across the app — avatars, tasks, and events.
              </p>
            </div>
            <div className="space-y-6">
              <ColorPicker value={color} onChange={setColor} />
              <Button className="w-full" size="lg" onClick={handleSaveColor} disabled={loading}>
                {loading ? <><Spinner size="sm" /> Saving…</> : 'Continue'}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Invite ───────────────────────────────────────────── */}
        {step === 2 && (
          <div>
            <div className="text-center mb-8">
              <div className="text-5xl mb-4">👋</div>
              <h1 className="text-2xl font-bold text-text-primary">Invite your family</h1>
              <p className="text-sm text-text-muted mt-2">
                Optional — you can invite more people later from Settings.
              </p>
            </div>

            {inviteLink ? (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary">Share this link with <strong>{inviteEmail}</strong>:</p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={inviteLink}
                    className="flex-1 rounded-base border border-border bg-background px-3 py-2 text-xs text-text-muted focus:outline-none"
                  />
                  <Button variant="secondary" size="sm" onClick={copyInviteLink}>Copy</Button>
                </div>
                <Button className="w-full" size="lg" onClick={() => setStep(3)}>
                  Continue
                </Button>
              </div>
            ) : (
              <form onSubmit={handleInvite} className="space-y-4">
                <Input
                  label="Email address"
                  type="email"
                  placeholder="partner@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  error={error}
                />
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-text-primary">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full rounded-base border border-border bg-surface px-4 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="admin">Admin — full access</option>
                    <option value="standard">Standard — most features</option>
                    <option value="limited">Limited — only assigned items</option>
                  </select>
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading ? <><Spinner size="sm" /> Creating invite…</> : 'Send invite'}
                </Button>
                <div className="text-center">
                  <button type="button" onClick={() => setStep(3)}
                    className="text-sm text-text-muted hover:text-primary transition-colors">
                    Skip for now →
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* ── Step 3: Choose plan ──────────────────────────────────────── */}
        {step === 3 && (
          <div>
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">✨</div>
              <h1 className="text-2xl font-bold text-text-primary">Choose a plan</h1>
              <p className="text-sm text-text-muted mt-2">
                Start free — upgrade anytime. Monthly and annual plans include a 7-day free trial.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {PLANS.map((plan) => {
                const isSelected = selectedPlan === plan.id
                return (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={cn(
                      'w-full text-left rounded-base border-2 p-4 transition-all',
                      isSelected
                        ? 'border-primary bg-primary-light'
                        : 'border-border bg-surface hover:border-primary/40'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn('font-semibold text-sm', isSelected ? 'text-primary' : 'text-text-primary')}>
                            {plan.name}
                          </span>
                          {plan.badge && (
                            <span className={cn(
                              'text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
                              plan.id === 'lifetime'
                                ? 'bg-violet-100 text-violet-700'
                                : 'bg-amber-100 text-amber-700'
                            )}>
                              {plan.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-muted mt-0.5">{plan.desc}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={cn('font-bold', isSelected ? 'text-primary' : 'text-text-primary')}>
                          {plan.price}
                        </span>
                        {plan.cycle && (
                          <span className="text-xs text-text-muted">{plan.cycle}</span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>

            {error && (
              <div className="rounded-base bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700 mb-4">
                {error}
              </div>
            )}

            <Button className="w-full" size="lg" onClick={handleChoosePlan} disabled={loading}>
              {loading
                ? <><Spinner size="sm" /> Redirecting…</>
                : PLANS.find((p) => p.id === selectedPlan)?.cta ?? 'Continue'
              }
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
